import { useState } from 'react';
import { Link2, Copy, Check, XCircle, QrCode } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { PhotoPolicyBadge } from '@/shared/ui/PhotoPolicyBadge';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatDate } from '@/lib/utils';
import {
  useProject,
  useUpdateProjectStatus,
  useCreateCaptureLink,
  useRevokeCaptureLink,
  NEXT_STATUS,
  STATUS_ACTION_LABEL,
} from './projects.api';
import type { ProjectStatus } from '@/shared/api/types';

export function ProjectDetailDialog({
  projectId,
  onClose,
}: {
  projectId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useProject(projectId);
  const updateStatus = useUpdateProjectStatus();
  const createLink = useCreateCaptureLink();
  const revokeLink = useRevokeCaptureLink();
  const push = useToast((s) => s.push);
  // Link nur im Speicher dieses Dialogs: er ist nach dem Schliessen weg,
  // wie das Backend ihn auch nie wieder herausgibt.
  const [captureUrl, setCaptureUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const onStatusChange = async (status: ProjectStatus) => {
    if (!projectId) return;
    try {
      await updateStatus.mutateAsync({ id: projectId, status });
      push('Status geändert.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Statuswechsel fehlgeschlagen.', 'error');
    }
  };

  const onCreateLink = async () => {
    if (!projectId) return;
    try {
      const res = await createLink.mutateAsync(projectId);
      setCaptureUrl(res.url);
      setCopied(false);
      push('Erfassungslink erzeugt.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Link konnte nicht erzeugt werden.', 'error');
    }
  };

  const onRevokeLink = async () => {
    if (!projectId) return;
    try {
      await revokeLink.mutateAsync(projectId);
      setCaptureUrl(null);
      push('Erfassungslink widerrufen.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Widerruf fehlgeschlagen.', 'error');
    }
  };

  const onCopy = async () => {
    if (!captureUrl) return;
    try {
      await navigator.clipboard.writeText(captureUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      push('Kopieren nicht möglich. Bitte manuell markieren.', 'error');
    }
  };

  const close = () => {
    setCaptureUrl(null);
    onClose();
  };

  return (
    <Dialog open={!!projectId} onClose={close} title={data?.title ?? 'Auftrag'}>
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            {data.object.photoPolicy && <PhotoPolicyBadge policy={data.object.photoPolicy} />}
            {data.reference && <span className="text-xs text-slate-500">{data.reference}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-500">Kunde</span>
              <p className="font-medium text-slate-900">{data.customer.companyName}</p>
            </div>
            <div>
              <span className="text-slate-500">Objekt</span>
              <p className="font-medium text-slate-900">{data.object.name}</p>
            </div>
            {(data.plannedStart || data.plannedEnd) && (
              <div className="col-span-2">
                <span className="text-slate-500">Zeitraum</span>
                <p className="text-slate-900">
                  {formatDate(data.plannedStart)} – {formatDate(data.plannedEnd)}
                </p>
              </div>
            )}
            {data.scopeOriginal && (
              <div className="col-span-2">
                <span className="text-slate-500">Vereinbarter Leistungsumfang</span>
                <p className="text-slate-900">{data.scopeOriginal}</p>
              </div>
            )}
            {data._count && (
              <div className="col-span-2 text-xs text-slate-500">
                {data._count.fieldReports} Meldung(en)
              </div>
            )}
          </div>

          {/* Erfassungslink */}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <QrCode className="h-4 w-4" /> Erfassungslink für den Außendienst
              </h3>
              {data.captureLinkActive && (
                <span className="text-xs text-green-600">aktiv</span>
              )}
            </div>

            {captureUrl ? (
              <div className="space-y-2">
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Dieser Link wird nur <strong>einmal</strong> angezeigt. Jetzt kopieren und an das
                  Team senden (z.&nbsp;B. per SMS).
                </div>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded bg-slate-100 px-2 py-1.5 text-xs">
                    {captureUrl}
                  </code>
                  <Button size="md" variant="secondary" onClick={onCopy}>
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Kopiert' : 'Kopieren'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button size="md" variant="secondary" onClick={onCreateLink} loading={createLink.isPending}>
                  <Link2 className="h-4 w-4" />
                  {data.captureLinkActive ? 'Neuen Link erzeugen' : 'Link erzeugen'}
                </Button>
                {data.captureLinkActive && (
                  <Button size="md" variant="ghost" onClick={onRevokeLink} loading={revokeLink.isPending}>
                    <XCircle className="h-4 w-4" /> Widerrufen
                  </Button>
                )}
              </div>
            )}
            {data.captureLinkActive && !captureUrl && (
              <p className="mt-2 text-xs text-slate-500">
                Ein aktiver Link existiert, kann aber nicht erneut angezeigt werden. „Neuen Link
                erzeugen" ersetzt ihn.
              </p>
            )}
          </div>

          {/* Statusuebergaenge nach Zustandsmaschine */}
          {NEXT_STATUS[data.status].length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Status ändern</h3>
              <div className="flex flex-wrap gap-2">
                {NEXT_STATUS[data.status].map((s) => (
                  <Button
                    key={s}
                    size="md"
                    variant={s === 'CLOSED' ? 'ghost' : 'secondary'}
                    onClick={() => onStatusChange(s)}
                    loading={updateStatus.isPending}
                  >
                    {STATUS_ACTION_LABEL[s]}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
